<template>
  <div class="desktop-client" :style="{ '--app-scale': appScale }">
    <!--
      Full-bleed backdrop, centred frame. The app is drawn once at the v2.1
      design size, 1440x900, and the whole frame is zoomed to fit the window
      (see fitToScreen), so nothing inside ever reflows or scrolls.
    -->
    <div
      ref="inner"
      class="desktop-client__inner"
      :class="{ 'desktop-client__inner--single': isSinglePane }"
      :style="{ zoom: appScale }"
    >
      <DesktopHeader class="desktop-client__header" :single-only="hasSingleArena">
        <!--
          The announcement ticker, the same component the v1 desktop runs in its
          right panel. Here it sits in the header as a pill between the settings
          and the profile. `tai-xiu.vue` already dispatches
          `announcement/updateAnnouncement` on mount, so it has its data.
        -->
        <template #ticker>
          <div ref="notifications" class="desktop-client__notifications">
            <Notifications
              popover
              :active-popover="tickerPopover ? tickerPopover.type : ''"
              @toggle-popover="toggleTickerPopover"
            />
            <NotificationPopover
              v-if="tickerPopover && tickerPopover.type === 'notification'"
              :left="TICKER_POPOVER_LEFT"
              :arrow-left="tickerPopover.anchor - TICKER_POPOVER_LEFT"
              @close="tickerPopover = null"
            />
            <SchedulePopover
              v-if="tickerPopover && tickerPopover.type === 'schedule'"
              :left="TICKER_POPOVER_LEFT"
              :arrow-left="tickerPopover.anchor - TICKER_POPOVER_LEFT"
              @close="tickerPopover = null"
            />
          </div>
        </template>
      </DesktopHeader>

      <div class="desktop-client__body">
        <SideRail class="desktop-client__rail" />

        <main class="desktop-client__panes">
          <template v-for="paneId in visiblePaneIds">
            <TaiXiuPane
              v-if="panes[paneId] && panes[paneId].product === 'tai-xiu'"
              :key="`${paneId}-tai-xiu`"
              :pane-id="paneId"
              :table-id="panes[paneId].id"
              :arena-items="arenaItemsFor(paneId)"
              :wide="isSinglePane"
              @select-arena="selectArena(paneId, $event)"
            />
            <CockfightPane
              v-else-if="panes[paneId]"
              :key="`${paneId}-cockfight`"
              :pane-id="paneId"
              :arena-id="panes[paneId].id"
              :arena-items="arenaItemsFor(paneId)"
              :active="hasUncollectTicket"
              :wide="isSinglePane"
              @select-arena="selectArena(paneId, $event)"
              @jackpot="openJackpot"
              @jackpot-physical="openJackpotPhysical"
            />
          </template>
        </main>
      </div>
    </div>

    <MenuPopup fixed :top-inset="popupTopInset" :left-inset="popupLeftInset" />
    <JackpotPopups ref="jackpotPopups" :top-inset="popupTopInset" :frame="appFrame" />
    <BetErrorPopup />
  </div>
</template>

<script>
import { some } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { disconnectPaneFightConnections, syncPaneFightConnections } from '@/utils/websocket';
import DesktopHeader from '@/components/desktop/desktop-header.vue';
import SideRail from '@/components/desktop/side-rail.vue';
import TaiXiuPane from '@/components/desktop/tai-xiu-pane.vue';
import CockfightPane from '@/components/desktop/cockfight-pane.vue';
import MenuPopup from '@/components/menus/menu-popup.vue';
import BetErrorPopup from '@/components/popup/bet-error-popup.vue';
import JackpotPopups from '@/components/desktop/jackpot-popups.vue';
import { PopupPage } from '@/utils/index.model';
import Notifications from '@/components/notification';
import NotificationPopover from '@/components/desktop/notification-popover.vue';
import SchedulePopover from '@/components/desktop/schedule-popover.vue';
import { DESKTOP_PANES_STORAGE_KEY } from '@/utils/constant';
import { getTableStatusClass } from '@/store/modules/tai-xiu';

/*
 * The desktop v2 client: header, persistent rail, and one or two game panes.
 *
 * Compact view: a pane is not tied to a product. Each pane shows whichever
 * arena it has selected -- a cockfight arena or a Tài Xỉu table -- from one
 * combined rail, so the two panes can hold two cockfights, two Tài Xỉu tables
 * or one of each. The only rule is that both panes cannot show the same
 * arena: whatever one pane shows is disabled in the other's rail.
 *
 * The selection lives here, not in the store, because nothing outside the
 * desktop client reads it; the panes bind their per-pane store state
 * (Tài Xỉu slip, cockfight scoped store) to the id they are given.
 */
/*
 * The v2.1 design frame (GA28 PrototypeV2.1.1): everything is laid out at
 * 1440x900 in fixed pixels and the frame is zoomed as a whole to the window.
 */
const FRAME_WIDTH = 1440;
const FRAME_HEIGHT = 900;
const HEADER_HEIGHT = 54;
/*
 * The cockfight bet panel's own scale (bet-panel/index.vue). The frame has
 * fixed widths, so the panes size the panel in CSS (cockfight-pane.vue) and it
 * is never transformed: a transform shrinks what is drawn but not the box, which
 * left gaps in a fixed-height column.
 */
const BET_PANEL_SCALE = 1;
const PANE_IDS = ['a', 'b'];
// Where each pane opens before the member picks anything: pane A on Tài Xỉu and
// pane B on cockfight, as the split view always started.
const DEFAULT_PRODUCT_BY_PANE = { a: 'tai-xiu', b: 'cockfight' };

// Which arena each pane shows survives a refresh (sessionStorage, like the rest
// of the app's persisted state), instead of resetting to the pane defaults.
// A `focus=tx` launch clears it (launch.vue) so the session opens on Tài Xỉu.
const PANES_STORAGE_KEY = DESKTOP_PANES_STORAGE_KEY;

const readStoredPanes = () => {
  const empty = { a: null, b: null };
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(PANES_STORAGE_KEY) || 'null');
    if (!stored) return empty;
    const read = (selection) =>
      selection && typeof selection.id === 'string' && ['cockfight', 'tai-xiu'].includes(selection.product)
        ? { product: selection.product, id: selection.id }
        : null;
    return { a: read(stored.a), b: read(stored.b) };
  } catch (e) {
    return empty;
  }
};

const arenaKey = (selection) => (selection ? `${selection.product}:${selection.id}` : '');

// Same mapping as the mobile arena rail (bet-panel/arena-status-indicator.vue).
// Tài Xỉu tables take the same palette through `getTableStatusClass`.
const statusClass = (status) => {
  switch (status) {
    case 'draw':
      return 'green';
    case 'banker':
      return 'red';
    case 'player':
      return 'blue';
    case 'confirmed':
    case 'closing':
      return 'orange';
    default:
      return '';
  }
};

export default {
  name: 'DesktopClient',
  components: {
    DesktopHeader,
    SideRail,
    TaiXiuPane,
    CockfightPane,
    MenuPopup,
    BetErrorPopup,
    JackpotPopups,
    Notifications,
    NotificationPopover,
    SchedulePopover,
  },
  data() {
    return {
      // paneId -> { product: 'cockfight' | 'tai-xiu', id } | null
      panes: { a: null, b: null },
      // The selection from before the last refresh, applied per pane as soon as
      // its arena exists (the cockfight and Tài Xỉu lists load separately).
      restoredPanes: readStoredPanes(),
      // Panes still on the default fallback -- not picked by the member nor
      // restored -- so they can move to their preferred product once its
      // arenas arrive (the two lists load separately).
      fallbackPanes: { a: false, b: false },
      // How far the page is scrolled, so the menu popup can stay under the
      // header instead of under where the header used to be.
      scrollY: 0,
      // Left edge of the GAME HELP button, relative to the app column: the
      // popup opens under it rather than against the column's right edge.
      popupLeftInset: null,
      // The app column's left/right edges in window pixels, so a popup fixed to
      // the window can still open inside the main layout.
      appFrame: { left: 0, right: 0, width: 0 },
      // { type: 'notification' | 'schedule', anchor } | null -- the ticker popover
      // that is open; `anchor` is its icon's centre from the ticker's left edge.
      tickerPopover: null,
      // The ticker popovers hug the app's left edge; only their arrow tracks the icon.
      TICKER_POPOVER_LEFT: 8,
      // CSS zoom on the 1440x900 frame so it fits the window (fitToScreen).
      appScale: 1,
      // Window pixels above the centred frame (a window wider than 16:10 leaves none).
      frameTop: 0,
    };
  },
  computed: {
    ...mapGetters({
      viewMode: 'taiXiu/viewMode',
      tables: 'taiXiu/tables',
      quickBetTable: 'taiXiu/quickBetTable',
      cockfightArenas: 'general/arenas',
      rootSelectedArena: 'general/selectedArena',
      token: 'general/token',
      oddsType: 'general/oddsType',
      inputPanelScaleRatio: 'general/inputPanelScaleRatio',
      isPopupOpen: 'menu/isPopupOpen',
      isJackpotPopupOpen: 'menu/isJackpotPopupOpen',
    }),
    ...mapGetters('jackpot', ['ownerTickets']),
    // Same rule as cock-fight.vue: an uncollected ticket lights up the jackpot
    // control inside BetPanel.
    hasUncollectTicket() {
      return some(this.ownerTickets, (ticket) => ticket.seenByMember === false);
    },
    /*
     * The menu popup opens under the header, as it does on v1. The popup is
     * fixed to the viewport, and the frame is centred in the window, so the
     * inset is the frame's top edge plus the zoomed header.
     */
    popupTopInset() {
      return Math.max(this.frameTop + HEADER_HEIGHT * this.appScale - this.scrollY, 0);
    },
    isSinglePane() {
      // The member chose one pane, or there is only one arena to show. The
      // frame is always 1440 wide in its own pixels, so width never forces it.
      return this.viewMode === 1 || this.hasSingleArena;
    },
    /*
     * Only one arena open: a second pane would have nothing to show, so the
     * view is single. The saved preference is left alone, so double view
     * comes back once a second arena does.
     */
    hasSingleArena() {
      return this.arenaOptions.filter((option) => !option.unavailable).length === 1;
    },
    visiblePaneIds() {
      return this.isSinglePane ? ['a'] : PANE_IDS;
    },
    /*
     * Every selectable arena, Tài Xỉu tables first, then cockfight (in the
     * order the mobile rail shows them). Each pane's rail is this list plus its own
     * active/disabled flags.
     */
    arenaOptions() {
      const cockfight = [...(this.cockfightArenas || [])].reverse().map((arena) => ({
        key: arenaKey({ product: 'cockfight', id: arena.id }),
        product: 'cockfight',
        id: arena.id,
        arenaId: arena.id,
        code: arena.code,
        country: arena.country,
        statusClass: statusClass(arena.fightStatus),
        unavailable: false,
      }));
      const taiXiu = (this.tables || []).map((table, index) => ({
        key: arenaKey({ product: 'tai-xiu', id: table.id }),
        product: 'tai-xiu',
        id: table.id,
        arenaId: table.arenaId,
        code: table.code,
        index,
        statusClass: getTableStatusClass(table),
        unavailable: table.isAvailable === false,
      }));
      return [...taiXiu, ...cockfight];
    },
    optionKeys() {
      return this.arenaOptions.map((option) => option.key);
    },
    // Arenas to hold a fights feed open for: what the panes and the quick-play card show.
    liveArenaIds() {
      const ids = this.visiblePaneIds
        .map((paneId) => this.arenaOptions.find((option) => option.key === arenaKey(this.panes[paneId])))
        .filter(Boolean)
        .map((option) => option.arenaId);
      if (this.quickBetTable && this.quickBetTable.arenaId) ids.push(this.quickBetTable.arenaId);
      return [...new Set(ids)];
    },
    liveFeedKey() {
      return [this.token, this.oddsType, ...this.liveArenaIds].join(':');
    },
  },
  watch: {
    /*
     * Re-measured on open, not only on mount: the header's widths settle after
     * the webfont loads, and a stale measurement left the panel's edge a few
     * pixels off the button.
     */
    isPopupOpen(open) {
      if (open) this.$nextTick(this.measurePopupAnchor);
    },
    isJackpotPopupOpen(open) {
      if (open) this.$nextTick(this.measurePopupAnchor);
    },
    /*
     * `general/inputPanelScaleRatio` defaults to 0 and is normally computed by
     * layouts/main-page.vue. The desktop client composes its own layout instead,
     * so without this the cockfight BetPanel renders at `transform: scale(0)` --
     * present in the DOM but invisible, which is exactly how the bet zone went
     * missing.
     */
    'inputPanelScaleRatio.landscape': {
      immediate: true,
      handler(ratio) {
        if (ratio === BET_PANEL_SCALE) return;
        this.updateGeneral({
          inputPanelScaleRatio: { ...this.inputPanelScaleRatio, landscape: BET_PANEL_SCALE },
        });
      },
    },
    // Arena lists arrive (and change) asynchronously; keep both panes on a
    // valid, distinct arena whenever they do, or when pane B comes back.
    optionKeys: {
      immediate: true,
      handler() {
        this.fillPanes();
      },
    },
    panes: {
      deep: true,
      handler(panes) {
        try {
          window.sessionStorage.setItem(PANES_STORAGE_KEY, JSON.stringify(panes));
        } catch (e) {
          // Storage unavailable: the selection just will not survive a refresh.
        }
      },
    },
    isSinglePane() {
      this.fillPanes();
    },
    tickerPopover(open, wasOpen) {
      if (!open === !wasOpen) return;
      const method = open ? 'addEventListener' : 'removeEventListener';
      document[method]('mousedown', this.onOutsideNotification);
    },
    liveFeedKey: {
      immediate: true,
      handler() {
        syncPaneFightConnections(this.token, this.oddsType, this.liveArenaIds);
      },
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.measurePopupAnchor, { passive: true });
    this.$nextTick(this.measurePopupAnchor);
    window.addEventListener('resize', this.fitToScreen, { passive: true });
    this.fitToScreen();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.fitToScreen);
    disconnectPaneFightConnections();
    document.removeEventListener('mousedown', this.onOutsideNotification);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.measurePopupAnchor);
  },
  methods: {
    onScroll() {
      this.scrollY = window.scrollY || window.pageYOffset || 0;
    },
    /*
     * PO: the whole app fits one screen, no page scroll. The frame is laid out
     * at its 1440x900 design size and zoomed as a whole -- every font, gap and
     * video scales together -- by the contain fit of the v2.1 prototype: as
     * large as fits both the window's width and its height, uncapped, so it
     * grows on large screens too.
     */
    fitToScreen() {
      this.appScale = Math.min(window.innerWidth / FRAME_WIDTH, window.innerHeight / FRAME_HEIGHT);
      this.frameTop = Math.max((window.innerHeight - FRAME_HEIGHT * this.appScale) / 2, 0);
    },
    measurePopupAnchor() {
      const inner = this.$el && this.$el.querySelector('.desktop-client__inner');
      if (inner) {
        const rect = inner.getBoundingClientRect();
        this.appFrame = {
          left: Math.max(Math.round(rect.left), 0),
          right: Math.max(Math.round(window.innerWidth - rect.right), 0),
          width: Math.round(rect.width),
        };
      }
      const anchor = inner && inner.querySelector('.desktop-header__link--help');
      if (!inner || !anchor) return;
      this.popupLeftInset = Math.max(
        Math.round(anchor.getBoundingClientRect().left - inner.getBoundingClientRect().left),
        0
      );
    },
    // Same icon closes its popover; the other icon switches to its own.
    toggleTickerPopover({ type, el }) {
      if (this.tickerPopover && this.tickerPopover.type === type) {
        this.tickerPopover = null;
        return;
      }
      const wrapper = this.$refs.notifications;
      if (!wrapper || !el) return;
      const icon = el.getBoundingClientRect();
      // Screen pixels back into the zoomed column's own pixels.
      const anchor = (icon.left + icon.width / 2 - wrapper.getBoundingClientRect().left) / this.appScale;
      this.tickerPopover = { type, anchor };
    },
    // The icons sit inside the wrapper and toggle themselves; anything else closes.
    onOutsideNotification(event) {
      const wrapper = this.$refs.notifications;
      if (wrapper && !wrapper.contains(event.target)) this.tickerPopover = null;
    },
    ...mapActions('general', ['updateGeneral']),
    ...mapActions('menu', ['updateMenu']),
    // The cockfight pane's jackpot pill, as on the v1 cockfight page.
    openJackpot() {
      this.$refs.jackpotPopups.open();
    },
    openJackpotPhysical() {
      this.updateMenu({ isPopupOpen: true, isJackpotPopupOpen: false, activePopupPage: PopupPage.jackpotPhysicalHelp });
    },
    otherPaneId(paneId) {
      return paneId === 'a' ? 'b' : 'a';
    },
    // The arena the other pane is showing, if that pane is on screen.
    takenKey(paneId, panes = this.panes) {
      const otherId = this.otherPaneId(paneId);
      return this.visiblePaneIds.includes(otherId) ? arenaKey(panes[otherId]) : '';
    },
    arenaItemsFor(paneId) {
      const ownKey = arenaKey(this.panes[paneId]);
      const takenKey = this.takenKey(paneId);
      return this.arenaOptions.map((option) => ({
        ...option,
        active: option.key === ownKey,
        takenByOtherPane: option.key === takenKey,
        disabled: option.key === takenKey || option.unavailable,
      }));
    },
    /*
     * Whether `paneId` may show `selection`: it exists and the other pane is not
     * showing it. A table going unavailable only blocks picking it -- a pane
     * already on it stays put, as the single-product panes did.
     */
    canShow(paneId, selection, panes = this.panes) {
      const key = arenaKey(selection);
      return this.optionKeys.includes(key) && key !== this.takenKey(paneId, panes);
    },
    isOpenOption(selection) {
      const option = this.arenaOptions.find((entry) => entry.key === arenaKey(selection));
      return !!option && !option.unavailable;
    },
    selectArena(paneId, item) {
      const selection = { product: item.product, id: item.id };
      const option = this.arenaOptions.find((entry) => entry.key === arenaKey(selection));
      if (arenaKey(selection) === arenaKey(this.panes[paneId])) return;
      if (!option || option.unavailable || !this.canShow(paneId, selection)) return;
      this.panes = { ...this.panes, [paneId]: selection };
      this.restoredPanes = { ...this.restoredPanes, [paneId]: null };
      this.fallbackPanes = { ...this.fallbackPanes, [paneId]: false };
    },
    // A pane on a fallback product that can now open on its preferred one.
    canUpgradeFallback(paneId, selection, panes) {
      const preferred = DEFAULT_PRODUCT_BY_PANE[paneId];
      if (!this.fallbackPanes[paneId] || !selection || selection.product === preferred) return false;
      return this.arenaOptions.some(
        (option) => option.product === preferred && !option.unavailable && this.canShow(paneId, option, panes)
      );
    },
    // First arena a pane can open on, preferring its default product and, for
    // cockfight, the arena the rest of the app already has selected.
    defaultSelection(paneId, panes) {
      const preferred = DEFAULT_PRODUCT_BY_PANE[paneId];
      const candidates = this.arenaOptions
        .filter((option) => !option.unavailable && this.canShow(paneId, option, panes))
        .sort((left, right) => Number(right.product === preferred) - Number(left.product === preferred));
      const rootArena = candidates.find(
        (option) => option.product === 'cockfight' && option.id === this.rootSelectedArena.id
      );
      const pick = preferred === 'cockfight' && rootArena ? rootArena : candidates[0];
      return pick ? { product: pick.product, id: pick.id } : null;
    },
    fillPanes() {
      const panes = { ...this.panes };
      const fallback = { ...this.fallbackPanes };
      // In order, so pane B's check sees what pane A has just settled on. Pane A
      // is checked for existence only: if the two collide (pane B coming back
      // after single-pane mode), it is pane B that moves.
      this.visiblePaneIds.forEach((paneId, index) => {
        const against = index === 0 ? {} : panes;
        const restored = this.restoredPanes[paneId];
        if (restored && this.isOpenOption(restored) && this.canShow(paneId, restored, against)) {
          panes[paneId] = restored;
          this.restoredPanes = { ...this.restoredPanes, [paneId]: null };
          fallback[paneId] = false;
          return;
        }
        const current = panes[paneId];
        if (current && this.canShow(paneId, current, against) && !this.canUpgradeFallback(paneId, current, against)) {
          return;
        }
        panes[paneId] = this.defaultSelection(paneId, against);
        fallback[paneId] = !!panes[paneId] && panes[paneId].product !== DEFAULT_PRODUCT_BY_PANE[paneId];
      });
      this.fallbackPanes = fallback;
      this.panes = panes;
    },
  },
};
</script>

<style lang="scss" scoped>
/*
 * v2.1 design frame (GA28 PrototypeV2.1.1): 1440x900. Header 54, then a 9px
 * gutter round and between everything: rail 288 | panes 1125, both 828 tall.
 * `$desktp-width` is the project's existing 1440 token (_breakpoint.scss).
 */
$header-height: 54px;
$frame-height: 900px;
$gutter: 9px;

.desktop-client {
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $ga-navy-950;
}

/*
 * The menu popup is fixed to the window, but the frame is centred -- so on a
 * wider screen the panel sat against the window's right edge, outside the app.
 * Bound its overlay to the same column the frame occupies and it lands on the
 * app's edge instead.
 */
.desktop-client ::v-deep .menu-popup-host--fixed .global-modal {
  left: 50%;
  right: auto;
  width: 100%;
  max-width: calc(#{$desktp-width} * var(--app-scale, 1));
  transform: translateX(-50%);
  // Clip the panel to this column so it is never seen outside the app.
  overflow: hidden;
}

.desktop-client__inner {
  flex: none;
  width: $desktp-width;
  height: $frame-height;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: $ga-frame-bg;
}

.desktop-client__header {
  flex: 0 0 $header-height;
  height: $header-height;
}

/*
 * The ticker pill inside the header: flexes into the header's spare width,
 * outlined in gold, with the v1 ticker centred in it. No overflow clip here --
 * the popovers hang from this box.
 */
.desktop-client__notifications {
  position: relative;
  /*
   * Above the panes, so the ticker popovers can hang over them. This is a
   * stacking context, so the popover's own z-index counts only inside it. The
   * header itself already sits above everything but the menu popup.
   */
  z-index: 2000;
  height: 32px;
  padding: 0 14px 0 12px;
  border: 1px solid $ga-gold-line;
  border-radius: 999px;
  background: rgba(3, 15, 24, 0.6);
  box-sizing: border-box;
  display: flex;
  align-items: center;

  // The ticker is the v1 component; only the 8px right margin it carries for
  // the v1 right panel is dropped, since here it runs to the pill's edge.
  ::v-deep .notification-container {
    margin-right: 0;
    color: $ga-text-default;
    font-weight: 500;

    // v2.1: 18px icons (30px boxes with the active rim), divider, then the text.
    img {
      width: 18px;
      height: 18px;
    }

    .lobby-trigger {
      top: 6px;
      left: 0;
      height: 18px;
      border-right-color: rgba(255, 255, 255, 0.18);
    }

    &.popover {
      .notification-trigger.active,
      .icon-schedule.active {
        border-color: $ga-gold-500;
        background-color: rgba(200, 162, 79, 0.12);
      }

      .schedule-trigger {
        left: 32px;
      }

      .notification-trigger {
        left: 66px;
      }

      &::after {
        top: 7px;
        left: 104px;
        height: 16px;
        background-color: rgba(255, 255, 255, 0.18);
      }

      .marquee {
        padding-left: 114px;
      }

      &.no-lobby {
        .schedule-trigger {
          left: 0;
        }

        .notification-trigger {
          left: 34px;
        }

        &::after {
          left: 72px;
        }

        .marquee {
          padding-left: 82px;
        }
      }
    }
  }
}

.desktop-client__body {
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
  padding: $gutter;
  display: flex;
  gap: $gutter;
  align-items: stretch;
}

.desktop-client__rail {
  flex: 0 0 auto;
  min-height: 0;
}

.desktop-client__panes {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  gap: $gutter;
  align-items: stretch;
}
</style>
